import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { EachNestingGroupData } from '../interfaces/multi-quota-nesting.interface';
import { AlertData } from '../domain/quota-nesting-alerts/alert';
import {
  QuotaNestingAlert,
  QuotaNestingAlertManager,
} from '../domain/quota-nesting-alerts/quota-nesting-alert-manager';

@Injectable()
export class MultiQuotaNestingService {
  private readonly _alertsManager = new QuotaNestingAlertManager();
  private _defaultSelectedNestingGroup: EachNestingGroupData = {
    uniqueId: '',
    id: '',
    state: 'pending',
    quotas: [],
    qualifications: [],
    selectedQualifications: [],
    titleState: 'view',
    qualificationSelection: false,
    canResetTable: false,
  };
  private _nestingGroupData$ = new BehaviorSubject<EachNestingGroupData[]>([]);
  private _selectedNestingGroup$ = new BehaviorSubject<EachNestingGroupData>(
    this._defaultSelectedNestingGroup
  );
  private readonly _hasUnsavedChanges$ = new BehaviorSubject<boolean>(false);
  private _createEmptySelectionStateCommand$ = new Subject<void>();

  constructor() {}

  public getAlerts$(): Observable<AlertData[]> {
    return this._alertsManager.alerts$;
  }

  public setAlertVisibility(name: QuotaNestingAlert, visible: boolean) {
    this._alertsManager.setVisibility(name, visible);
  }

  public setNestingGroupData(
    groupId: string,
    groupData: EachNestingGroupData
  ): void {
    const data = this._nestingGroupData$.value;
    const existingIndex = data.findIndex((v) => v.id === groupId);
    if (existingIndex !== -1) {
      data[existingIndex] = groupData;
      this._nestingGroupData$.next(data);

      if (this._selectedNestingGroup$.value.id === groupId) {
        this.setSelectedNestingGroup(groupData);
      }
      return;
    }
    this._nestingGroupData$.next([...data, groupData]);
  }

  public deleteNestingGroupData(groupIdToDelete: string) {
    const data = this._nestingGroupData$.value;
    const updatedData = data.filter((group) => group.id !== groupIdToDelete);
    this._nestingGroupData$.next(updatedData);
    if (this._selectedNestingGroup$.value.id === groupIdToDelete) {
      const existingSelectedIndex = data.findIndex(
        (group) => group.id === groupIdToDelete
      );
      const nextSelectedIndex =
        existingSelectedIndex - 1 < 0
          ? updatedData.length - 1
          : existingSelectedIndex - 1;
      updatedData[nextSelectedIndex] &&
        this._selectedNestingGroup$.next(updatedData[nextSelectedIndex]);
    }
  }

  public replaceNestingGroupData(
    at: number,
    replacement: EachNestingGroupData
  ) {
    const data = this._nestingGroupData$.value;
    const isSelected = data[at].id === this._selectedNestingGroup$.value.id;
    data[at] = replacement;
    this._nestingGroupData$.next(data);
    if (isSelected) {
      this._selectedNestingGroup$.next(replacement);
    }
  }

  public getNestingGroupData(): Observable<EachNestingGroupData[]> {
    return this._nestingGroupData$.asObservable();
  }

  public setSelectedNestingGroup(selected: EachNestingGroupData): void {
    this._selectedNestingGroup$.next(selected);
  }

  public getSelectedNestingGroup(): Observable<EachNestingGroupData> {
    return this._selectedNestingGroup$.asObservable();
  }

  public hasUnsavedChanges(): Observable<boolean> {
    return this._hasUnsavedChanges$.asObservable();
  }

  public indicateUnsavedChanges(): void {
    this._hasUnsavedChanges$.next(true);
  }

  public sendEmptySelectionStateCreationCommand(): void {
    this._createEmptySelectionStateCommand$.next();
  }

  public getEmptySelectionStateCreationCommand(): Observable<void> {
    return this._createEmptySelectionStateCommand$.asObservable();
  }

  public resetData(): void {
    this._nestingGroupData$.next([]);
    this._selectedNestingGroup$.next(this._defaultSelectedNestingGroup);
    this._hasUnsavedChanges$.next(false);
  }
}
